import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { ROUTES } from 'utils/constants';
import { CreateSalesRequest, Customer, PaymentMethod } from 'types/sales';
import { usePOS } from 'contexts/POSContext';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { storePendingTransaction, CartItem } from 'utils/posIndexedDBUtils';
import Button from 'components/Button';
import Toast from 'components/Toast';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  CreditCard,
  Package,
} from 'lucide-react';
import styles from './Payment.module.scss';

interface PaymentLocationState {
  cart: CartItem[];
  customerId: string;
  subtotal: number;
}

function Payment() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as PaymentLocationState | null;
  const customerId = state?.customerId ?? null;

  // Get active company ID for storing transactions
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);

  // Use POS context for sales options (shared across POS pages)
  const {
    paymentMethods,
    products,
    customers,
    loading,
    error: salesOptionsError,
    setCart,
    setSelectedCustomer,
    setCustomerSearchQuery,
  } = usePOS();

  const selectedCustomerDetails = useMemo<Customer | null>(() => {
    if (!customerId) {
      return null;
    }
    return customers.find((customer) => customer.id === customerId) || null;
  }, [customers, customerId]);

  const [amountPaid, setAmountPaid] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNumpadModal, setShowNumpadModal] = useState(false);
  const [tempAmount, setTempAmount] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Redirect if no state - do this first, before loading data
  const hasState = useMemo(
    () => !!(state && state.cart && state.cart.length > 0),
    [state],
  );

  useEffect(() => {
    if (!hasState) {
      navigate(ROUTES.POS.ROOT, { replace: true });
    }
  }, [hasState, navigate]);

  // Default payment method selection
  const defaultPaymentMethod =
    paymentMethods.find((pm) => pm.alias.toLowerCase().includes('cash')) ||
    paymentMethods[0] ||
    null;

  // Set default payment method when sales options are loaded
  useEffect(() => {
    if (defaultPaymentMethod && !selectedPaymentMethod) {
      setSelectedPaymentMethod(defaultPaymentMethod);
    }
  }, [defaultPaymentMethod, selectedPaymentMethod]);

  // Show error toast if sales options failed to load
  useEffect(() => {
    if (salesOptionsError) {
      showToast(salesOptionsError, 'error');
    }
  }, [salesOptionsError, showToast]);

  // Set exact amount as preselected when state is available
  useEffect(() => {
    if (state && state.subtotal) {
      setAmountPaid(state.subtotal.toFixed(2));
    }
  }, [state]);

  // Handle payment submission - must be before early return (React Hooks rule)
  const handleSubmit = useCallback(async () => {
    if (!state || !state.cart || state.cart.length === 0) {
      return;
    }

    const { cart, customerId, subtotal: stateSubtotal } = state;
    const amountPaidNum = parseFloat(amountPaid) || 0;
    const change = amountPaidNum - stateSubtotal;

    if (amountPaidNum < stateSubtotal) {
      showToast('Amount paid must be at least equal to the total', 'error');
      return;
    }
    if (!selectedPaymentMethod) {
      showToast('Please select a payment method', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const requestData: CreateSalesRequest = {
        date: today,
        customerId,
        productEntries: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
        cashOrArEntries: [
          {
            categoryId: selectedPaymentMethod.categoryId,
            amount: stateSubtotal,
          },
        ],
      };

      // Save to IndexedDB first (offline-first approach)
      if (!activeCompanyId) {
        throw new Error('No company selected');
      }
      const localId = await storePendingTransaction(
        requestData,
        activeCompanyId,
      );

      // Clear POS state
      setCart([]);
      setSelectedCustomer(null);
      setCustomerSearchQuery('');

      // Navigate immediately to success page (offline-first UX)
      // The Success page will handle syncing to prevent duplicate calls
      navigate(ROUTES.POS.SUCCESS, {
        state: {
          saleId: localId, // Use local ID initially
          total: stateSubtotal,
          subtotal: stateSubtotal,
          change,
          amountPaid: amountPaidNum,
          cart,
          customer: selectedCustomerDetails ?? null,
          paymentMethodName: selectedPaymentMethod.alias,
          transactionDate: new Date().toISOString(),
          isLocal: true, // Flag to indicate this is a local transaction
        },
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to process payment. Please try again.';
      showToast(errorMessage, 'error');
      setIsSubmitting(false);
    }
  }, [
    state,
    amountPaid,
    selectedPaymentMethod,
    navigate,
    showToast,
    selectedCustomerDetails,
    activeCompanyId,
    setCart,
    setSelectedCustomer,
    setCustomerSearchQuery,
  ]);

  // Quick amount buttons: first option is exact subtotal, then rounded options based on denominations
  // Must be before early return to follow Rules of Hooks
  // Currency denominations - can be edited based on country's currency denominations
  const denominations = [10, 20, 50, 100, 500, 1000];
  const subtotal = state?.subtotal || 0;
  const roundUpToNearest = (num: number, multiple: number) =>
    Math.ceil(num / multiple) * multiple;
  const quickAmounts = [
    subtotal, // Exact amount
    ...denominations
      .slice()
      .reverse()
      .map((denomination) => roundUpToNearest(subtotal, denomination)),
  ];

  // Calculate change
  const amountPaidNum = parseFloat(amountPaid) || 0;
  const change = amountPaidNum - subtotal;

  // Format currency function
  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  // Handle numeric input
  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Allow only numbers and decimal point
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setAmountPaid(value);
      }
    },
    [],
  );

  // Handle numpad button clicks (for modal)
  const handleNumpadClick = useCallback((value: string) => {
    if (value === 'clear') {
      setTempAmount('');
      return;
    }

    if (value === 'backspace') {
      setTempAmount((prev) => prev.slice(0, -1));
      return;
    }

    if (value === '.') {
      // Only allow one decimal point
      setTempAmount((prev) => {
        if (!prev.includes('.')) {
          return prev + '.';
        }
        return prev;
      });
      return;
    }

    // Handle numbers
    setTempAmount((prev) => {
      const newValue = prev + value;
      // Validate: allow only numbers and one decimal point, max 2 decimal places
      if (/^\d*\.?\d{0,2}$/.test(newValue)) {
        return newValue;
      }
      return prev;
    });
  }, []);

  // Handle custom button click - open modal
  const handleCustomClick = useCallback(() => {
    setAmountPaid('');
    setTempAmount('');
    setShowNumpadModal(true);
  }, []);

  // Handle modal close - apply amount
  const handleModalClose = useCallback(() => {
    setShowNumpadModal(false);
  }, []);

  // Handle modal apply - set the amount and close
  const handleModalApply = useCallback(() => {
    setAmountPaid(tempAmount);
    setShowNumpadModal(false);
  }, [tempAmount]);

  // Check if prerequisites are met (customers can be created dynamically, so skip check)
  const hasPaymentMethods = paymentMethods.length > 0;
  const hasProducts = products.length > 0;
  const isSetupIncomplete = !loading && (!hasPaymentMethods || !hasProducts);

  // Early return if no state - show redirecting message (after all hooks)
  if (!state || !state.cart || state.cart.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>Redirecting...</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>Loading...</div>
      </div>
    );
  }

  if (isSetupIncomplete) {
    return (
      <div className={styles.container}>
        <div className={styles.setupErrorState}>
          <div className={styles.prerequisitesCard}>
            <h2 className={styles.prerequisitesTitle}>
              Before you can use the POS
            </h2>
            <p className={styles.prerequisitesDescription}>
              Please setup the checklist to get started using the POS:
            </p>
            <ul className={styles.prerequisitesList}>
              <li className={styles.prerequisitesItem}>
                {hasPaymentMethods ? (
                  <CheckCircle2 size={20} className={styles.checkIcon} />
                ) : (
                  <Circle size={20} className={styles.circleIcon} />
                )}
                <div className={styles.prerequisitesItemContent}>
                  <div className={styles.prerequisitesItemTitle}>
                    <CreditCard size={18} />
                    <span>Set up Payment Methods</span>
                  </div>
                  {!hasPaymentMethods && (
                    <p className={styles.prerequisitesItemDescription}>
                      You need at least one payment method to record how
                      customers pay for their purchases (e.g., Cash, Credit
                      Card, Bank Transfer).
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(ROUTES.DASHBOARD)}
                    className={styles.prerequisitesButton}
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </li>
              <li className={styles.prerequisitesItem}>
                {hasProducts ? (
                  <CheckCircle2 size={20} className={styles.checkIcon} />
                ) : (
                  <Circle size={20} className={styles.circleIcon} />
                )}
                <div className={styles.prerequisitesItemContent}>
                  <div className={styles.prerequisitesItemTitle}>
                    <Package size={18} />
                    <span>Set up Products or Services</span>
                  </div>
                  {!hasProducts && (
                    <p className={styles.prerequisitesItemDescription}>
                      You need at least one product or service to sell. Products
                      are items you sell, and services are non-physical
                      offerings.
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(ROUTES.PRODUCTS.ROOT)}
                    className={styles.prerequisitesButton}
                  >
                    Go to Products
                  </Button>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const { cart } = state;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Order Summary */}
        <div className={styles.summarySection}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(ROUTES.POS.ROOT)}
            aria-label="Go back to make changes"
          >
            <ArrowLeft size={20} />
            <span className={styles.backButtonText}>
              Go back to make changes
            </span>
            <span className={styles.backButtonTextShort}>Go Back</span>
          </button>
          <h2 className={styles.summaryTitle}>Order Summary</h2>
          <div className={styles.cartItems}>
            {cart.map((item) => (
              <div key={item.productId} className={styles.cartItem}>
                <div className={styles.cartItemName}>{item.productName}</div>
                <div className={styles.cartItemDetails}>
                  <span>
                    {item.quantity} × {formatCurrency(item.price)}
                  </span>
                  <span className={styles.cartItemTotal}>
                    {formatCurrency(item.price * item.quantity)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.totalRow}>
            <span>Total:</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
        </div>

        {/* Payment Section */}
        <div className={styles.paymentSection}>
          <h2>Payment</h2>
          <div className={styles.paymentContent}>
            <div className={styles.paymentMethodGroup}>
              <label htmlFor="paymentMethod">Payment Method</label>
              <select
                id="paymentMethod"
                value={selectedPaymentMethod?.id || ''}
                onChange={(e) => {
                  const pm = paymentMethods.find(
                    (p) => p.id === e.target.value,
                  );
                  setSelectedPaymentMethod(pm || null);
                }}
                className={styles.paymentMethodSelect}
              >
                {paymentMethods.map((pm) => (
                  <option key={pm.id} value={pm.id}>
                    {pm.alias}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.amountInputGroup}>
              <label htmlFor="amountPaid">Amount Paid</label>
              <input
                id="amountPaid"
                type="text"
                inputMode="decimal"
                value={amountPaid}
                onChange={handleAmountChange}
                placeholder="0.00"
                className={styles.amountInput}
                disabled
                readOnly
                autoComplete="off"
              />
            </div>

            <div className={styles.quickAmounts}>
              {quickAmounts.map((amount, index) => (
                <button
                  key={index}
                  type="button"
                  className={styles.quickAmountButton}
                  onClick={() => setAmountPaid(amount.toFixed(2))}
                >
                  {index === 0 ? 'Exact Amount' : formatCurrency(amount)}
                </button>
              ))}
              <button
                type="button"
                className={styles.quickAmountButton}
                onClick={handleCustomClick}
              >
                Custom
              </button>
            </div>

            {/* Custom Numpad Modal */}
            <Modal
              show={showNumpadModal}
              onHide={handleModalClose}
              centered
              className={styles.numpadModal}
            >
              <Modal.Body className={styles.numpadModalBody}>
                <div className={styles.numpadAmountDisplay}>
                  {formatCurrency(parseFloat(tempAmount) || 0)}
                </div>
                <div className={styles.numpad}>
                  <div className={styles.numpadRow}>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('1')}
                    >
                      1
                    </button>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('2')}
                    >
                      2
                    </button>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('3')}
                    >
                      3
                    </button>
                  </div>
                  <div className={styles.numpadRow}>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('4')}
                    >
                      4
                    </button>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('5')}
                    >
                      5
                    </button>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('6')}
                    >
                      6
                    </button>
                  </div>
                  <div className={styles.numpadRow}>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('7')}
                    >
                      7
                    </button>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('8')}
                    >
                      8
                    </button>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('9')}
                    >
                      9
                    </button>
                  </div>
                  <div className={styles.numpadRow}>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('.')}
                    >
                      .
                    </button>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('0')}
                    >
                      0
                    </button>
                    <button
                      type="button"
                      className={styles.numpadButton}
                      onClick={() => handleNumpadClick('backspace')}
                    >
                      ⌫
                    </button>
                  </div>
                  <div className={styles.numpadRow}>
                    <button
                      type="button"
                      className={`${styles.numpadButton} ${styles.numpadClearButton}`}
                      onClick={() => handleNumpadClick('clear')}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className={`${styles.numpadButton} ${styles.numpadCancelButton}`}
                      onClick={handleModalClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`${styles.numpadButton} ${styles.numpadOkButton}`}
                      onClick={handleModalApply}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </Modal.Body>
            </Modal>

            <div className={styles.changeSection}>
              <div className={styles.changeRow}>
                <span>Change:</span>
                <span
                  className={`${styles.changeAmount} ${
                    change < 0 ? styles.negativeChange : ''
                  }`}
                >
                  {formatCurrency(Math.max(0, change))}
                </span>
              </div>
              {change < 0 && (
                <div className={styles.changeWarning}>
                  Insufficient payment amount
                </div>
              )}
            </div>

            <Button
              onClick={() => void handleSubmit()}
              disabled={isSubmitting || amountPaidNum < subtotal || change < 0}
              className={styles.submitButton}
            >
              {isSubmitting ? 'Processing...' : 'Complete Payment'}
            </Button>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Payment;
